import { BlockConfigData } from './types'

const wrapInCommonStyles = (content: string, data: BlockConfigData): string => {
  const bgColor = data.backgroundColor || 'transparent'
  const paddingTop = data.paddingTop || '20px'
  const paddingBottom = data.paddingBottom || '20px'
  const paddingLeft = data.paddingLeft || '20px'
  const paddingRight = data.paddingRight || '20px'
  const align = data.align || 'center'
  
  const borderWidth = data.borderWidth || '0px'
  const borderStyle = data.borderStyle || 'solid'
  const borderColor = data.borderColor || '#dddddd'
  const borderRadius = data.borderRadius || '0px'

  const borderCss = borderWidth !== '0px' ? `border: ${borderWidth} ${borderStyle} ${borderColor};` : ''
  const borderRadiusCss = borderRadius !== '0px' ? `border-radius: ${borderRadius};` : ''

  return `
    <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background-color: ${bgColor};">
      <tr>
        <td align="${align}" style="padding: ${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft};">
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="max-width: 900px; margin: 0 auto; ${borderCss} ${borderRadiusCss} overflow: hidden;">
            <tr>
              <td>
                ${content}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `
}

export const generateBlockHTML = (blockId: string, data: BlockConfigData): string => {
  let content = ''
  
  switch (blockId) {
    case 'header_simple':
      content = `
        <div align="center" style="padding: 0;">
          ${data.logoUrl ? `<img src="${data.logoUrl}" alt="Logo" style="width: ${data.logoWidth || '150px'}; height: ${data.logoHeight || 'auto'}; display: block; margin: 0 auto;" />` : '<div style="font-family: sans-serif; font-size: 24px; font-weight: bold; color: #333;">Logo</div>'}
        </div>
      `
      break;

    case 'social_links':
      const networks = [
        { key: 'socialFb', icon: 'https://img.icons8.com/color/48/facebook-new.png', label: 'Facebook' },
        { key: 'socialIg', icon: 'https://img.icons8.com/color/48/instagram-new.png', label: 'Instagram' },
        { key: 'socialIn', icon: 'https://img.icons8.com/color/48/linkedin.png', label: 'LinkedIn' },
        { key: 'socialX', icon: 'https://img.icons8.com/color/48/twitterx--v1.png', label: 'X' },
        { key: 'socialYt', icon: 'https://img.icons8.com/color/48/youtube-play.png', label: 'YouTube' },
        { key: 'socialWa', icon: 'https://img.icons8.com/color/48/whatsapp.png', label: 'WhatsApp' },
      ]
      
      const iconSize = data.socialSize || '32px'
      const activeNetworks = networks.filter(n => data[n.key as keyof BlockConfigData])
      
      if (activeNetworks.length === 0) {
        content = ''
        break
      }
      
      const iconsHtml = activeNetworks.map(n => `
        <td style="padding: 0 8px;">
          <a href="${data[n.key as keyof BlockConfigData]}" target="_blank">
            <img src="${n.icon}" alt="${n.label}" width="${iconSize}" style="display: block; border: 0;" />
          </a>
        </td>
      `).join('')
      
      content = `
        <div align="${data.align || 'center'}" style="padding: 0;">
          <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="display: inline-table;">
            <tr>
              ${iconsHtml}
            </tr>
          </table>
        </div>
      `
      break;

    case 'footer_copyright':
      content = `
        <div align="center" style="font-family: sans-serif; font-size: 14px; line-height: 1.6; color: #6c757d;">
          <p style="margin: 0 0 10px 0;">
            ${data.copyrightText || `© ${new Date().getFullYear()} Todos los derechos reservados.`}
          </p>
          <p style="margin: 0;">
            <a href="{{unsubscribe_url}}" style="color: #007bff; text-decoration: underline;">
              ${data.unsubscribeText || 'Cancelar suscripción'}
            </a>
          </p>
        </div>
      `
      break;

    case 'hero': {
      const heroAlign = data.align || 'center'
      // Build hero image styles (email-safe alignment via margin)
      let heroImageStyle = 'display: block;'
      if (data.imageWidth) {
        heroImageStyle += ` width: ${data.imageWidth};`
      } else {
        heroImageStyle += ' width: 100%; max-width: 900px;'
      }
      if (data.imageHeight) {
        heroImageStyle += ` height: ${data.imageHeight};`
      } else {
        heroImageStyle += ' height: auto;'
      }
      if (heroAlign === 'center') {
        heroImageStyle += ' margin-left: auto; margin-right: auto;'
      } else if (heroAlign === 'right') {
        heroImageStyle += ' margin-left: auto; margin-right: 0;'
      } else {
        heroImageStyle += ' margin-left: 0; margin-right: auto;'
      }

      content = `
        <div style="text-align: ${heroAlign};">
          ${data.imageUrl ? `<img src="${data.imageUrl}" alt="${data.imageAlt || 'Hero'}" style="${heroImageStyle}" />` : ''}
          <div style="padding: ${data.padding || '40px 30px'}; text-align: ${heroAlign};">
            ${data.title ? `<h1 style="margin: 0 0 20px 0; color: #333; font-size: 36px; font-family: sans-serif;">${data.title}</h1>` : ''}
            ${data.subtitle ? `<p style="margin: 0 0 25px 0; color: #666; font-size: 22px; line-height: 1.6; font-family: sans-serif;">${data.subtitle}</p>` : ''}
            ${data.buttonText && data.buttonUrl ? `
              <a href="${data.buttonUrl}" style="background-color: ${data.buttonColor || '#007bff'}; color: #ffffff; padding: 12px 25px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold; font-family: sans-serif;">
                ${data.buttonText}
              </a>
            ` : ''}
          </div>
        </div>
      `;
      break;
    }

    case '1_col':
      content = `
        <div style="font-family: sans-serif; font-size: 20px; line-height: 1.6; color: #333333; text-align: ${data.align || 'left'};">
          ${data.content || ''}
        </div>
      `;
      break;

      
    case '2_cols':
      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            <td width="${data.col1Width || '50%'}" valign="top" style="padding: 10px; background-color: ${data.col1BackgroundColor || 'transparent'}; border: ${data.col1Border || 'none'}; text-align: ${data.col1Align || 'left'};">
              <div style="font-family: sans-serif; font-size: 14px; line-height: 1.5; color: #444444; text-align: ${data.col1Align || 'left'};">
                ${data.col1Content || ''}
              </div>
            </td>
            <td width="${data.col2Width || '50%'}" valign="top" style="padding: 10px; background-color: ${data.col2BackgroundColor || 'transparent'}; border: ${data.col2Border || 'none'}; text-align: ${data.col2Align || 'left'};">
              <div style="font-family: sans-serif; font-size: 14px; line-height: 1.5; color: #444444; text-align: ${data.col2Align || 'left'};">
                ${data.col2Content || ''}
              </div>
            </td>
          </tr>
        </table>
      `;
      break;
      
    case '3_cols':
      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            <td width="${data.col1Width || '33.33%'}" valign="top" style="padding: 10px; background-color: ${data.col1BackgroundColor || 'transparent'}; border: ${data.col1Border || 'none'}; text-align: ${data.col1Align || 'left'};">
              <div style="font-family: sans-serif; font-size: 14px; line-height: 1.4; color: #444444; text-align: ${data.col1Align || 'left'};">
                ${data.col1Content || ''}
              </div>
            </td>
            <td width="${data.col2Width || '33.33%'}" valign="top" style="padding: 10px; background-color: ${data.col2BackgroundColor || 'transparent'}; border: ${data.col2Border || 'none'}; text-align: ${data.col2Align || 'left'};">
              <div style="font-family: sans-serif; font-size: 14px; line-height: 1.4; color: #444444; text-align: ${data.col2Align || 'left'};">
                ${data.col2Content || ''}
              </div>
            </td>
            <td width="${data.col3Width || '33.33%'}" valign="top" style="padding: 10px; background-color: ${data.col3BackgroundColor || 'transparent'}; border: ${data.col3Border || 'none'}; text-align: ${data.col3Align || 'left'};">
              <div style="font-family: sans-serif; font-size: 14px; line-height: 1.4; color: #444444; text-align: ${data.col3Align || 'left'};">
                ${data.col3Content || ''}
              </div>
            </td>
          </tr>
        </table>
      `;
      break;

    case 'image_text': {
      const imgSide = data.imagePosition || 'left'
      // Column width percentage (20–80%)
      const imgPct = Math.min(80, Math.max(20, parseInt(data.imageWidth as string) || 40))
      const txtPct = 100 - imgPct
      const imgWidthAttr = `${imgPct}%`
      const txtWidthAttr = `${txtPct}%`
      const imgBorderRadius = data.imageBorderRadius || '0px'

      // Build image element attributes (email-safe: both HTML attrs + CSS)
      const imgHeightAttr = data.imageHeight ? ` height="${data.imageHeight}"` : ''
      const imgHeightCss = data.imageHeight ? `height:${data.imageHeight};` : 'height:auto;'

      // Email-safe placeholder (no flexbox)
      const imgPlaceholder = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            <td align="center" style="background-color:#eeeeee; height:150px; font-family:Arial,sans-serif; font-size:14px; color:#999999;">
              Image
            </td>
          </tr>
        </table>
      `

      const imgAlign = data.align || 'center'

      const imgCell = `
        <td width="${imgWidthAttr}" valign="middle" align="${imgAlign}" style="padding:10px; width:${imgWidthAttr}; text-align:${imgAlign};">
          ${data.imageUrl
            ? `<img src="${data.imageUrl}" alt="${data.imageAlt || ''}"${imgHeightAttr} style="max-width:100%; ${imgHeightCss} border-radius:${imgBorderRadius}; display:inline-block; border:0;" />`
            : imgPlaceholder
          }
        </td>
      `
      
      const btnPadding = data.buttonPaddingVertical ? `${data.buttonPaddingVertical} ${data.buttonPaddingHorizontal}` : '10px 20px'
      const btnColor = data.buttonColor || '#007bff'
      const btnRadius = data.borderRadius || '5px'
      const btnFontSize = data.fontSize || '14px'

      const btnHtml = data.buttonText && data.buttonUrl
        ? `<div style="margin-top: 15px;">
             <a href="${data.buttonUrl}" style="background-color:${btnColor}; color:#ffffff; padding:${btnPadding}; text-decoration:none; border-radius:${btnRadius}; display:inline-block; font-weight:bold; font-family:Arial,Helvetica,sans-serif; font-size:${btnFontSize};">
               ${data.buttonText}
             </a>
           </div>`
        : ''

      const textCell = `
        <td width="${txtWidthAttr}" valign="middle" style="padding:10px; width:${txtWidthAttr}; font-family:Arial,Helvetica,sans-serif;">
          ${data.title ? `<h3 style="margin:0 0 10px 0; color:#333333; font-size:18px; line-height:1.3;">${data.title}</h3>` : ''}
          ${data.content ? `<p style="margin:0 0 15px 0; color:#666666; font-size:14px; line-height:1.6;">${data.content}</p>` : ''}
          ${btnHtml}
        </td>
      `

      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            ${imgSide === 'left' ? imgCell + textCell : textCell + imgCell}
          </tr>
        </table>
      `
      break;
    }


    case 'button':
      const btnPadding = `${data.buttonPaddingVertical || '12px'} ${data.buttonPaddingHorizontal || '25px'}`
      const alignment = data.align || 'center'
      const placement = data.buttonPlacement || 'below'
      
      const titleHtml = data.title ? `<h2 style="margin: 0 0 10px 0; font-family: sans-serif; font-size: 24px; color: #333;">${data.title}</h2>` : ''
      const contentHtml = data.content ? `<div style="margin: 0 0 20px 0; font-family: sans-serif; font-size: 16px; color: #666; line-height: 1.5;">${data.content}</div>` : ''
      const buttonHtml = `
        <a href="${data.buttonUrl || '#'}" style="background-color: ${data.buttonColor || '#007bff'}; color: ${data.color || '#ffffff'}; padding: ${btnPadding}; text-decoration: none; border-radius: ${data.borderRadius || '5px'}; display: inline-block; font-weight: bold; font-family: sans-serif; font-size: ${data.fontSize || '16px'}; ${data.buttonWidth ? `width: ${data.buttonWidth}; box-sizing: border-box; text-align: center;` : ''}">
          ${data.buttonText || 'Click Here'}
        </a>
      `

      if (alignment === 'center') {
        const first = placement === 'above' ? buttonHtml : (titleHtml + contentHtml)
        const second = placement === 'above' ? (titleHtml + contentHtml) : buttonHtml
        const spacing = (placement === 'above' && data.title) ? '20px' : '0'
        
        content = `
          <div style="text-align: center;">
            <div style="margin-bottom: ${spacing};">${first}</div>
            <div>${second}</div>
          </div>
        `
      } else {
        const textCell = `<td style="vertical-align: middle;">${titleHtml}${contentHtml}</td>`
        const buttonCell = `<td style="vertical-align: middle; padding-${alignment === 'left' ? 'left' : 'right'}: 20px;" width="1%" nowrap>${buttonHtml}</td>`
        const firstCell = alignment === 'left' ? textCell : buttonCell
        const secondCell = alignment === 'left' ? buttonCell : textCell
        
        content = `
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              ${firstCell}
              ${secondCell}
            </tr>
          </table>
        `
      }
      break;
      
    case 'divider':
      content = `<hr style="border: 0; border-top: 1px solid #dee2e6; margin: 0;" />`
      break;
      
    case 'spacer':
      const spacerHeight = data.spacerHeight || '40px'
      content = `<div style="height: ${spacerHeight};">&nbsp;</div>`
      break;
      
    case 'image': {
      if (!data.imageUrl) {
        content = ''
        break
      }

      const imgAlign = data.align || 'center'

      const imageStyles: string[] = []
      if (data.imageWidth) {
        imageStyles.push(`width: ${data.imageWidth}`)
      } else {
        imageStyles.push('max-width: 100%')
      }
      if (data.imageHeight) {
        imageStyles.push(`height: ${data.imageHeight}`)
      } else {
        imageStyles.push('height: auto')
      }
      imageStyles.push('display: block')
      imageStyles.push(`border-radius: ${data.borderRadius || '0px'}`)
      // Email-safe alignment for block elements (text-align does NOT work on display:block)
      if (imgAlign === 'center') {
        imageStyles.push('margin-left: auto; margin-right: auto')
      } else if (imgAlign === 'right') {
        imageStyles.push('margin-left: auto; margin-right: 0')
      } else {
        imageStyles.push('margin-left: 0; margin-right: auto')
      }
      imageStyles.push('border: 0')

      const imageStyleString = imageStyles.join('; ')

      // Wrap in table with align attribute (Outlook-safe fallback for centering)
      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            <td align="${imgAlign}" style="font-size:0; line-height:0;">
              <img src="${data.imageUrl}" alt="${data.imageAlt || ''}" style="${imageStyleString};" />
            </td>
          </tr>
        </table>
      `
      break;
    }

    case 'icon_note': {
      const iconSymbol = data.iconSymbol || '⚠️'
      const iconSize = data.iconSize || '34px'
      const iconWidth = Math.min(40, Math.max(10, parseInt(data.iconWidth as string) || 18))
      const textWidth = 100 - iconWidth
      const iconColor = data.iconColor || '#f59e0b'
      const titleColor = data.titleColor || '#1d4f91'
      const contentColor = data.contentColor || '#1f2937'

      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>
            <td width="${iconWidth}%" valign="top" style="width: ${iconWidth}%; padding: 0 16px 0 0;" align="center">
              <div style="font-size: ${iconSize}; line-height: 1; color: ${iconColor};">${iconSymbol}</div>
            </td>
            <td width="${textWidth}%" valign="top" style="width: ${textWidth}%; font-family: Arial, sans-serif;">
              ${data.title ? `<div style="font-size: 24px; line-height: 1.25; font-weight: 700; color: ${titleColor}; margin: 0 0 8px 0;">${data.title}</div>` : ''}
              ${data.content ? `<div style="font-size: 18px; line-height: 1.6; color: ${contentColor}; margin: 0;">${data.content}</div>` : ''}
            </td>
          </tr>
        </table>
      `
      break
    }

    case 'feature_list': {
      const count = data.featureCount || 3
      const layout = data.featureLayout || 'vertical'
      const iconColor = data.featureIconColor || '#007bff'
      const iconSize = data.featureIconSize || '36px'

      const features = [
        { icon: data.feature1Icon || '✅', title: data.feature1Title || '', text: data.feature1Text || '' },
        { icon: data.feature2Icon || '✅', title: data.feature2Title || '', text: data.feature2Text || '' },
        { icon: data.feature3Icon || '✅', title: data.feature3Title || '', text: data.feature3Text || '' },
        { icon: data.feature4Icon || '✅', title: data.feature4Title || '', text: data.feature4Text || '' },
      ].slice(0, count)

      if (layout === 'vertical') {
        const colWidth = `${Math.floor(100 / count)}%`
        const cells = features.map(f => `
          <td width="${colWidth}" valign="top" align="center" style="padding: 12px; width: ${colWidth};">
            <div style="font-size: ${iconSize}; margin-bottom: 10px; color: ${iconColor};">${f.icon}</div>
            ${f.title ? `<div style="font-family: Arial, sans-serif; font-weight: bold; font-size: 15px; color: #333333; margin-bottom: 6px;">${f.title}</div>` : ''}
            ${f.text ? `<div style="font-family: Arial, sans-serif; font-size: 13px; color: #666666; line-height: 1.5;">${f.text}</div>` : ''}
          </td>
        `).join('')

        content = `
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
            <tr>${cells}</tr>
          </table>
        `
      } else {
        // horizontal: icon left, text right, stacked rows
        const rows = features.map(f => `
          <tr>
            <td width="56" valign="top" align="center" style="padding: 8px 12px 8px 0; width: 56px;">
              <div style="font-size: ${iconSize}; color: ${iconColor};">${f.icon}</div>
            </td>
            <td valign="top" style="padding: 8px 0;">
              ${f.title ? `<div style="font-family: Arial, sans-serif; font-weight: bold; font-size: 15px; color: #333333; margin-bottom: 4px;">${f.title}</div>` : ''}
              ${f.text ? `<div style="font-family: Arial, sans-serif; font-size: 13px; color: #666666; line-height: 1.5;">${f.text}</div>` : ''}
            </td>
          </tr>
        `).join('')

        content = `
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
            ${rows}
          </table>
        `
      }
      break
    }

    case 'card_grid': {
      const cardCount = data.cardCount || 2
      const cardWidth = cardCount === 3 ? '33.33%' : '50%'

      const cards = [
        { image: data.card1Image, title: data.card1Title, text: data.card1Text, btnText: data.card1ButtonText, btnUrl: data.card1ButtonUrl },
        { image: data.card2Image, title: data.card2Title, text: data.card2Text, btnText: data.card2ButtonText, btnUrl: data.card2ButtonUrl },
        { image: data.card3Image, title: data.card3Title, text: data.card3Text, btnText: data.card3ButtonText, btnUrl: data.card3ButtonUrl },
      ].slice(0, cardCount)

      const cardCells = cards.map(c => `
        <td width="${cardWidth}" valign="top" style="padding: 8px; width: ${cardWidth};">
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
            <tr>
              <td>
                ${c.image ? `<img src="${c.image}" alt="${c.title || ''}" style="width: 100%; display: block; height: auto; border: 0;" />` : ''}
                <div style="padding: 16px; font-family: Arial, sans-serif;">
                  ${c.title ? `<div style="font-weight: bold; font-size: 16px; color: #333333; margin-bottom: 8px;">${c.title}</div>` : ''}
                  ${c.text ? `<div style="font-size: 13px; color: #666666; line-height: 1.5; margin-bottom: 12px;">${c.text}</div>` : ''}
                  ${c.btnText && c.btnUrl ? `<a href="${c.btnUrl}" style="background-color: ${data.buttonColor || '#007bff'}; color: #ffffff; padding: 8px 16px; text-decoration: none; border-radius: 4px; display: inline-block; font-size: 13px; font-weight: bold;">${c.btnText}</a>` : ''}
                </div>
              </td>
            </tr>
          </table>
        </td>
      `).join('')

      content = `
        <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
          <tr>${cardCells}</tr>
        </table>
      `
      break
    }

    case 'testimonial': {
      const style = data.quoteStyle || 'simple'

      const avatarHtml = data.quoteAvatarUrl
        ? `<img src="${data.quoteAvatarUrl}" alt="${data.quoteAuthor || ''}" style="width: 56px; height: 56px; border-radius: 50%; display: block; margin: 0 auto 8px auto; border: 0;" />`
        : ''

      const authorBlock = (data.quoteAuthor || data.quoteRole) ? `
        <div style="margin-top: 16px;">
          ${avatarHtml}
          ${data.quoteAuthor ? `<div style="font-family: Arial, sans-serif; font-weight: bold; font-size: 14px; color: #333333;">${data.quoteAuthor}</div>` : ''}
          ${data.quoteRole ? `<div style="font-family: Arial, sans-serif; font-size: 13px; color: #888888;">${data.quoteRole}</div>` : ''}
        </div>
      ` : ''

      if (style === 'card') {
        content = `
          <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="border-left: 4px solid ${data.buttonColor || '#007bff'}; background-color: #f8f9fa; padding: 20px 24px; border-radius: 4px;">
                <div style="font-family: Georgia, serif; font-size: 16px; color: #444444; font-style: italic; line-height: 1.7;">"${data.quoteText || ''}"</div>
                ${authorBlock}
              </td>
            </tr>
          </table>
        `
      } else {
        content = `
          <div style="text-align: center; padding: 10px;">
            <div style="font-family: Georgia, serif; font-size: 60px; color: #dddddd; line-height: 0.8; margin-bottom: 16px;">"</div>
            <div style="font-family: Georgia, serif; font-size: 17px; color: #444444; font-style: italic; line-height: 1.7; max-width: 600px; margin: 0 auto;">${data.quoteText || ''}</div>
            ${authorBlock}
          </div>
        `
      }
      break
    }

    default:
      content = ''
  }

  return wrapInCommonStyles(content, data)
}
